import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { BoltIcon } from "../components/Bolts";
import { CONTACT_EMAIL } from "../data/catalog";
import { Mail, Send, CheckCircle2, MapPin } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            toast.error("Please fill in your name, email, and message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contacts`, { ...form, page: "/contact" });
            setSuccess(true);
            setForm({ name: "", email: "", message: "" });
            toast.success("Message sent.");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not send right now. Try again.";
            toast.error(typeof detail === "string" ? detail : "Send failed.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page" className="relative">
            <section className="relative border-b border-white/10">
                <div className="absolute inset-0 hero-bg opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-storm-void/70 to-storm-void" />
                <div className="relative container-x py-24 md:py-32">
                    <p className="eyebrow flex items-center gap-2 mb-5">
                        <BoltIcon className="w-3.5 h-3.5" /> Contact
                    </p>
                    <h1 className="text-5xl md:text-7xl lg:text-[7rem] text-white leading-[0.85]">
                        Let's <span className="text-storm-bolt">talk shop.</span>
                    </h1>
                    <p className="text-storm-mist text-base md:text-lg lg:text-xl mt-6 max-w-2xl">
                        Custom requests, questions, weird ideas, or just want to chat about
                        a project — drop us a line.
                    </p>
                </div>
            </section>

            <section className="container-x py-20 md:py-28 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5 space-y-6">
                    <div className="surface-solid p-7 space-y-5">
                        <p className="eyebrow mb-1">Direct</p>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="flex items-center gap-3 text-white hover:text-storm-bolt transition-colors"
                            data-testid="contact-email-link"
                        >
                            <Mail className="w-5 h-5 text-storm-bolt" />
                            <span className="font-mono text-sm break-all">{CONTACT_EMAIL}</span>
                        </a>
                        <div className="flex items-center gap-3 text-white/80 text-sm">
                            <MapPin className="w-5 h-5 text-storm-bolt" />
                            Made to order · Ships worldwide
                        </div>
                    </div>
                    <div className="surface-solid p-7">
                        <p className="eyebrow mb-3">Response time</p>
                        <p className="text-storm-mist leading-relaxed text-sm">
                            Most messages are answered within one business day. For custom
                            colors, sizes or designs, use the{" "}
                            <a href="#contact-us-now" className="text-storm-bolt underline-offset-2 hover:underline">
                                Contact Us Now
                            </a>{" "}
                            section at the bottom of any page — same inbox.
                        </p>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {success ? (
                        <div
                            data-testid="contact-success"
                            className="surface-solid p-10 text-center relative overflow-hidden"
                        >
                            <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-storm-bolt/10 blur-3xl rounded-full pointer-events-none" />
                            <CheckCircle2 className="w-14 h-14 text-storm-bolt mx-auto mb-5" />
                            <h2 className="text-3xl md:text-4xl text-white mb-3">Message sent.</h2>
                            <p className="text-storm-mist">
                                Thanks for reaching out. We'll be in touch shortly.
                            </p>
                            <button
                                onClick={() => setSuccess(false)}
                                className="btn-secondary mt-8"
                                data-testid="contact-another-btn"
                            >
                                Send Another
                            </button>
                        </div>
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            data-testid="contact-form"
                            className="surface-solid p-6 md:p-10 space-y-6"
                        >
                            <h2 className="text-3xl md:text-4xl text-white">Send a Message</h2>
                            <div className="grid sm:grid-cols-2 gap-5">
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                        Name *
                                    </label>
                                    <Input
                                        name="name"
                                        value={form.name}
                                        onChange={onChange}
                                        data-testid="contact-input-name"
                                        placeholder="Jane Storm"
                                        className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none h-12 focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt"
                                    />
                                </div>
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                        Email *
                                    </label>
                                    <Input
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="contact-input-email"
                                        placeholder="you@example.com"
                                        className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none h-12 focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-[0.2em] text-storm-mist block mb-2">
                                    Message *
                                </label>
                                <Textarea
                                    name="message"
                                    rows={7}
                                    value={form.message}
                                    onChange={onChange}
                                    data-testid="contact-input-message"
                                    placeholder="Tell us what's on your mind…"
                                    className="bg-storm-void border-white/10 text-white placeholder:text-storm-mist/60 rounded-none focus-visible:ring-storm-bolt focus-visible:ring-offset-0 focus-visible:border-storm-bolt resize-none"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="contact-submit-btn"
                                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                <Send className="w-4 h-4" />
                                {submitting ? "Sending…" : "Send Message"}
                            </button>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
};

export default ContactPage;
